import type { ContactFormContent } from '@/components/ContactForm'
import { COUNTRIES, COUNTRIES_TR } from '@/content/countries'
import { INDUSTRIES, INDUSTRIES_TR } from '@/content/industries'

/**
 * The closing form at the bottom of every page.
 *
 * Asked for on 2026-08-30: "bizim main page ve contact us sayfamız dışındaki her
 * sayfada bu şekilde bir form olsun", with a screenshot of digitopia.co's own
 * gated form. It replaces the gradient card that used to sit there — a headline
 * and a button back to Contact Us — so a reader can start the conversation on the
 * page that convinced them instead of being sent somewhere else to start it.
 *
 * The fields are Contact Us' own (`content/pageForms.ts`), including the message
 * box — asked for on 2026-08-31: every contact form on the site offers one, and
 * none of them insists on it. So it is the last field here and it carries no
 * `required`: a reader who only wants the callback leaves it empty, and a reader
 * who has something to say has somewhere to say it without changing page.
 *
 * **Only four are required** — first name, last name, business email and
 * country. Company Name was required too until 2026-08-31, when it was asked
 * to stop being mandatory across every form on the site; the field stays,
 * unstarred. Job Title is asked for and not insisted on, because every extra
 * asterisk on a form somebody meets at the bottom of an e-book page is a reason
 * to close the tab.
 *
 * A locale that has not been written falls back to the English version, which is
 * the ordinary rule for this dictionary. Turkish is written below; its Country
 * list is `COUNTRIES_TR` and its sector names are translated, because these are
 * the two fields a Turkish reader has to pick their own answer out of.
 */

const CONSENT_EN =
  'I would like to receive communications about products and offerings from Digitopia.'

type ContactField = ContactFormContent['fields'][number]

/**
 * The message box, on its own so a page that adds a question of its own can put
 * that question *above* it — Partner Program and Solutions both do. A form that
 * ends on "anything else?" reads right; one that ends on a tick list after the
 * message box does not.
 */
export const MESSAGE_FIELD_EN: ContactField = {
  name: 'message',
  label: 'Message',
  type: 'textarea',
}

export const MESSAGE_FIELD_TR: ContactField = {
  name: 'message',
  label: 'Mesajınız',
  type: 'textarea',
}

/** Everything before the message box, for the forms that insert a field there. */
export const BAND_FIELDS_EN: ContactField[] = [
  { name: 'firstname', label: 'First Name', type: 'text', required: true },
    { name: 'lastname', label: 'Last Name', type: 'text', required: true },
    { name: 'email', label: 'Business Email', type: 'email', required: true },
    { name: 'company', label: 'Company Name', type: 'text' },
    { name: 'jobtitle', label: 'Job Title', type: 'text' },
    { name: 'phone', label: 'Phone Number', type: 'tel' },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      placeholder: 'Please Select',
      options: COUNTRIES,
    },
    {
      name: 'industry',
      label: 'Industry',
      type: 'select',
      placeholder: 'Please Select',
      options: INDUSTRIES,
      other: { label: 'Other', placeholder: 'Which industry?' },
    },
]

export const BAND_FORM_EN: ContactFormContent = {
  fields: [...BAND_FIELDS_EN, MESSAGE_FIELD_EN],
  consent: CONSENT_EN,
  submit: 'Submit',
  privacy: {
    before: 'By clicking Submit, I agree to the use of my personal information according to the ',
    link: { label: 'Digitopia Privacy Statement', href: '/en/privacy-policy' },
    after:
      '. I understand that my personal information may be transferred for processing outside my country of residence, where standards of data protection may be different.',
  },
  confirmation:
    'Thank you. We read every message. Expect a reply from a practice lead within two business days.',
  /*
    The two answers after a submit, dictated word for word on 2026-08-30: one for
    a gated download ("we've sent the download link to your email") and one for
    an ordinary enquiry ("our team has received your message"). Which shows is
    decided by whether a Download button is what scrolled the reader here.
  */
  thanks: {
    download: [
      'Thank you for your interest! We’ve sent the download link to your email.',
      'Please check your inbox (and your spam folder, just in case) to access the materials.',
      'You can also download it directly using the button below.',
    ],
    contact: [
      'Thank you for getting in touch with us.',
      'Our team has received your message and will get back to you shortly.',
    ],
    help: {
      before: 'If you have any questions or need further assistance, ',
      link: 'contact us',
      after: ' here.',
      href: '/en/contact-us',
    },
    downloadLabel: 'Download now',
    requiredNote: 'Please choose an option.',
  },
}

export const BAND_FIELDS_TR: ContactField[] = [
    { name: 'firstname', label: 'Ad', type: 'text', required: true },
    { name: 'lastname', label: 'Soyad', type: 'text', required: true },
    { name: 'email', label: 'Kurumsal e-posta', type: 'email', required: true },
    { name: 'company', label: 'Şirket adı', type: 'text' },
    { name: 'jobtitle', label: 'Göreviniz', type: 'text' },
    { name: 'phone', label: 'Telefon', type: 'tel' },
    {
      name: 'country',
      label: 'Ülke',
      type: 'select',
      placeholder: 'Lütfen seçin',
      options: COUNTRIES_TR,
    },
    {
      name: 'industry',
      label: 'Sektör',
      type: 'select',
      placeholder: 'Lütfen seçin',
      options: INDUSTRIES_TR,
      other: { label: 'Diğer', placeholder: 'Hangi sektör?' },
    },
]

export const BAND_FORM_TR: ContactFormContent = {
  fields: [...BAND_FIELDS_TR, MESSAGE_FIELD_TR],
  consent: 'Digitopia’nın ürün ve hizmetleriyle ilgili bilgilendirmeleri almak istiyorum.',
  submit: 'Gönder',
  privacy: {
    before: 'Gönder’e tıklayarak kişisel verilerimin ',
    link: { label: 'Digitopia Gizlilik Bildirimi', href: '/en/privacy-policy' },
    after:
      '’ne uygun şekilde işlenmesini kabul ediyorum. Verilerimin, koruma standartları farklı olabilecek şekilde yaşadığım ülke dışında işlenmek üzere aktarılabileceğini biliyorum.',
  },
  confirmation:
    'Teşekkürler. Her mesajı okuyoruz; iki iş günü içinde bir practice lead size dönecek.',
  thanks: {
    download: [
      'İlginiz için teşekkürler! İndirme bağlantısını e-postanıza gönderdik.',
      'Materyallere ulaşmak için gelen kutunuzu (ve ihtimale karşı spam klasörünüzü) kontrol edin.',
      'Dilerseniz aşağıdaki butondan doğrudan da indirebilirsiniz.',
    ],
    contact: [
      'Bize ulaştığınız için teşekkürler.',
      'Mesajınız ekibimize ulaştı; kısa süre içinde size döneceğiz.',
    ],
    help: {
      before: 'Sorunuz olursa ya da yardıma ihtiyaç duyarsanız, ',
      link: 'bize buradan ulaşın',
      after: '.',
      href: '/en/contact-us',
    },
    downloadLabel: 'Şimdi indir',
    requiredNote: 'Lütfen bir seçim yapın.',
  },
}
